import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LabVisitsService } from "@/lib/api_client";
import { useAuth } from "@/lib/auth";
import { impersonateHeaders } from "@/lib/api";

export type FollowupSuggestion = { question: string; rationale: string };

export type ConsultationFollowupSuggestionRow = {
  id: string;
  labVisitId: string;
  suggestions: FollowupSuggestion[];
  aiInputSnapshot: string | null;
  model: string;
  generatedBy: string | null;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
};

// Both hooks answer only an impersonation token. The panel mounts under the
// anamnesis page's impersonation gate, as useConsultationNotes does.
export const useConsultationFollowupSuggestions = (
  labVisitId: string | null | undefined,
) => {
  const { impersonateToken } = useAuth();
  return useQuery<ConsultationFollowupSuggestionRow | null>({
    queryKey: ["consultation-followup-suggestions", labVisitId],
    queryFn: async () => {
      // refetch() ignores `enabled`; a request without the token would 401
      // and the interceptor would log the operator out.
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res =
        await LabVisitsService.getApiAdminLabVisitsByIdFollowupSuggestions(
          {
            path: { id: labVisitId! },
            headers: impersonateHeaders(impersonateToken),
          },
        );
      if (!res.data || res.data.status !== "success")
        throw new Error("Klarte ikke å hente forslag");
      const row = res.data.row as ConsultationFollowupSuggestionRow | null;
      return row;
    },
    enabled: !!impersonateToken && !!labVisitId,
    staleTime: 5 * 60_000,
    retry: false,
  });
};

export const useRegenerateConsultationFollowupSuggestions = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation<ConsultationFollowupSuggestionRow, Error, string>({
    mutationFn: async (labVisitId) => {
      if (!impersonateToken) throw new Error("Ikke impersonert");
      const res =
        await LabVisitsService.postApiAdminLabVisitsByIdFollowupSuggestionsRegenerate(
          {
            path: { id: labVisitId },
            headers: impersonateHeaders(impersonateToken),
          },
        );
      if (!res.data || res.data.status !== "success")
        throw new Error("Klarte ikke å regenerere forslag");
      return res.data.row as ConsultationFollowupSuggestionRow;
    },
    onSuccess: (data, labVisitId) => {
      qc.setQueryData(
        ["consultation-followup-suggestions", labVisitId],
        data,
      );
    },
  });
};
